import React from 'react';
import PropTypes from 'prop-types';
import { ApHeaderBar } from '@alixpartners/ui-components';
import { Route, Redirect, Switch } from 'react-router-dom';
import numeral from 'numeral';
import 'numeral/locales';
import Highcharts from 'highcharts';
import HighchartsAdapt from 'highcharts-adapt-chart-to-legend';
import HighchartsMore from 'highcharts/highcharts-more';
import { appConfig } from 'configs';
import Nav from '../Nav';
import Download from '../Download';
import Settings from '../Settings';
import Explore from '../Explore';
import Benchmarks from '../Benchmarks';
import UnpublishedData from '../Explore/UnpublishedData';
import { TaxonomyViewer } from '../TaxonomyViewer';
import ConfigureDpaas from 'Data/ConfigureDpaas';
import { DIRECT_TAXONOMY_STATUS, UPLOAD_STATUS } from 'Data/constants';
import LoadingIndicator from 'components/LoadingIndicator';
import Notifications from 'components/Notifications';
import { ApiErrorNotification } from 'components/ApiErrorNotification';
import DataContext from './Context/DataContext';
import { QueryStringContextProvider } from '../contexts/QueryStringContext/QueryStringContextProvider';
import { ApiDataContextProvider } from '../contexts/ApiDataContext/ApiDataContextProvider';
import { SpendCubeSettingsContextProvider } from '../contexts/SpendCubeSettingsContext/SpendCubeSettingsContextProvider';
import { ClassificationContextProvider } from '../contexts/ClassificationContext/ClassificationContextProvider';
import { SpendCubeSettingsContext } from '../contexts/SpendCubeSettingsContext';
import { SettingsContext } from '../contexts/SettingsContext';
import { SettingsOptionsContext } from '../contexts/SettingsOptionsContext';
import { ClassificationContext } from '../contexts/ClassificationContext';
import { SettingsContextProvider } from '../contexts/SettingsContext/SettingsContextProvider';
import { SettingsOptionsContextProvider } from '../contexts/SettingsOptionsContext/SettingsOptionsContextProvider';
import { MetadataContextProvider } from '../contexts/MetadataContext/MetadataContextProvider';
import { UploadStateContextProvider } from '../contexts/UploadStateContext/UploadStateContextProvider';
import { PermissionsContextProvider } from '../contexts/PermissionsContext/PermissionsContextProvider';
import { MetadataContext } from '../contexts/MetadataContext';
import { UploadStateContext } from '../contexts/UploadStateContext';
import DefaultHeader from 'components/DefaultHeader';
import Data from 'Data';
import NoData from 'Data/NoData';
import TaxonomyApplication from 'TaxonomyMapper/TaxonomyApplication';
import Initiatives from 'Initiatives';
import BudgetTracker from 'BudgetTracker';
import Waves from 'Waves';
import InitiativesToWavesAllocation from 'InitiativesToWavesAllocation';
import './style.css';
import {
	PermissionsContext,
	PERMISSIONS,
} from '../contexts/PermissionsContext';
import {
	ApAuthenticationContextConsumer,
	ApRedirectRoute,
} from '@alixpartners/ui-utils';
import { withRouter } from 'react-router';
import {
	getStateFromLocation,
	omitDataContextFromPath,
} from 'utils/query-string';
import { HeaderTitle, Main, HeaderWrapper } from './styles';
import Pmi from 'Pmi';
import Investigate from 'Investigate';
import { DirectTaxonomyMapper, IndirectTaxonomyMapper } from 'TaxonomyMapper';
import TaxonomyPreview from 'TaxonomyMapperPreview';
import { isDemoMode } from 'utils/apiRequests';
import { UploadVendorStateContextProvider } from 'contexts/UploadVendorStateContext/UploadVendorStateContextProvider';
import PivotTable from 'PivotTable';
import CategoryManagement from 'CategoryManagement';
import { AccessDenied } from 'components/AccessDenied';
import { UploadCategoryStateContextProvider } from 'contexts/UploadCategoryStateContext/UploadCategoryStateContextProvider';
import SupplierDiversity from 'SupplierDiversity/SupplierDiversity';
import ApVendorLibrary from 'ApVendorLibrary/ApVendorLibrary';
import { UploadSkuStateContextProvider } from 'contexts/UploadSkuStateContext/UploadSkuStateContextProvider';
import SpendController from 'SpendController';
import SpeedToMarket from 'SpeedToMarket';
// import LandingPage from 'LandingPage/LandingPage';
numeral.locale(appConfig.locale);
HighchartsAdapt(Highcharts);
HighchartsMore(Highcharts);

class AppBase extends React.Component {
	static propTypes = {
		user: PropTypes.shape({
			userName: PropTypes.string.isRequired,
			profile: PropTypes.object.isRequired,
		}).isRequired,
	};

	state = {
		dataContext: {
			id: null,
			active: false,
		},
	};
	historyUnsubscribe;

	componentWillMount() {
		const { history, location } = this.props;

		this.historyUnsubscribe = history.listen(this.handleHistoryChange);
		// handle first load of application
		// get the context from the URL
		const { context } = getStateFromLocation(location);
		this.handleUpdateDataContext(context);
	}

	handleHistoryChange = location => {
		const { history } = this.props;
		const { context } = getStateFromLocation(location);
		const { state: locationState } = location;

		if (locationState) {
			const { isRewriteUrl = false, bookmarks = [] } = locationState;
			const isPresentationMode = bookmarks.length > 0;
			// update the context when this app is in presentation mode
			// and the URL has not been rewritten
			if (!isRewriteUrl && isPresentationMode) {
				this.handleUpdateDataContext(context);
			}
		}

		// TODO: `history.replace` to be removed
		if (context) {
			const path = omitDataContextFromPath(
				`${window.location.pathname}${window.location.search}`
			);
			// rewrite the URL without the context
			history.replace(
				path,
				locationState
					? { ...locationState, isRewriteUrl: true }
					: { isRewriteUrl: true }
			);
		}
	};

	componentWillUnmount() {
		this.historyUnsubscribe();
	}

	handleDataContextStatus = active =>
		this.setState(prevState => ({
			dataContext: { ...prevState.dataContext, active },
		}));

	handleUpdateDataContext = id =>
		this.setState({
			dataContext: { id, active: typeof id === 'string' },
		});

	render() {
		// const { location } = this.props;
		const { dataContext } = this.state;
		const { REACT_APP_CURRENCY } = process.env;
		const { initial } = appConfig;
		const appName = <span style={{ fontSize: '16px' }}>Spend X-Ray</span>;
		let baseUrl = '/';
		try {
			const appUrls = JSON.parse(process.env.REACT_APP_URLS);
			baseUrl = appUrls.Main.Ui;
		} catch (e) {
			console.error('can not parse REACT_APP_URLS', e);
		}
		// const landingPagePath = '/introduction';
		// const isLandingPage = location.pathname === landingPagePath;
		return (
			<React.Fragment>
				<Switch>
					<Route
						path="/accessdenied"
						render={({ location }) => (
							<React.Fragment>
								<DefaultHeader />
								<Main>
									<AccessDenied
										name={location.state && location.state.aadName}
										url={location.state && location.state.aadUrl}
									/>
								</Main>
							</React.Fragment>
						)}
					/>
					<PermissionsContextProvider>
						<ClassificationContextProvider>
							<UploadVendorStateContextProvider>
								<UploadSkuStateContextProvider>
									<UploadCategoryStateContextProvider>
										<UploadStateContextProvider>
											<MetadataContextProvider>
												<ApiDataContextProvider>
													<PermissionsContext.Consumer>
														{({ permissions }) => (
															<UploadStateContext.Consumer>
																{({ status, refetch }) => (
																	<ClassificationContext.Consumer>
																		{({
																			config,
																			refetch: fetchClassification,
																		}) => (
																			<React.Fragment>
																				<Route
																					render={routeProps => (
																						<Notifications
																							{...routeProps}
																							onMessage={refetch}
																						/>
																					)}
																				/>

																				<MetadataContext.Consumer>
																					{({ metadata }) => {
																						if (!metadata || !status) {
																							return (
																								<LoadingIndicator
																									loading={true}
																								/>
																							);
																						}

																						const isTaxonomyApplying =
																							status.directApplyTaxonomyState ===
																							DIRECT_TAXONOMY_STATUS.IN_PROGRESS;
																						const isTaxonomyApplyCompleted =
																							status.directApplyTaxonomyState ===
																							DIRECT_TAXONOMY_STATUS.COMPLETED;
																						const isTaxonomyApplyNotStarted =
																							status.directApplyTaxonomyState ===
																							DIRECT_TAXONOMY_STATUS.NOT_STARTED;
																						const isUploadState =
																							!status.isDataPublished ||
																							status.state ===
																								UPLOAD_STATUS.CLASSIFIED;
																						const defaultCurrency =
																							REACT_APP_CURRENCY ||
																							metadata.currencies[0].currency;
																						const defaultCurrencyType = metadata.currencies.find(
																							el => {
																								return (
																									el.currency ===
																										REACT_APP_CURRENCY || true
																								);
																							}
																						).currencyTypes[0];

																						const analysisPageUrl = permissions.includes(
																							PERMISSIONS.SpendControllerUser
																						)
																							? '/spend-controller/preworkshop-template'
																							: '/spend';

																						// to redirect user & portco user to spend
																						const isSendToData = permissions.includes(
																							PERMISSIONS.DataRedirect
																						);

																						return (
																							<SettingsContextProvider
																								defaultCurrency={
																									defaultCurrency
																								}
																								defaultCurrencyType={
																									defaultCurrencyType
																								}
																							>
																								<SettingsOptionsContextProvider>
																									<SettingsOptionsContext.Consumer
																									>
																										{({
																											options,
																											refetch: fetchOptions,
																										}) => (
																											<SpendCubeSettingsContextProvider
																											>
																												<SpendCubeSettingsContext.Consumer
																												>
																													{({
																														settings,
																														refetch: fetchSpendCube,
																													}) => (
																														<SettingsContext.Consumer
																														>
																															{({
																																companyName,
																															}) => {
																																const {
																																	spendCubeCurrency,
																																	spendCubeDateRangeId,
																																} = settings;
																																const dateRange =
																																	options.dateRanges.find(
																																		item =>
																																			item.id ===
																																			spendCubeDateRangeId
																																	) ||
																																	options
																																		.dateRanges[0];

																																return (
																																	<QueryStringContextProvider
																																		defaultCurrency={
																																			spendCubeCurrency ||
																																			defaultCurrency
																																		}
																																		defaultCurrencyType={
																																			defaultCurrencyType
																																		}
																																		defaultUnit={
																																			initial.unit
																																		}
																																		defaultDateRangeId={
																																			spendCubeDateRangeId
																																				? spendCubeDateRangeId.toString()
																																				: ''
																																		}
																																		defaultDateFrom={
																																			dateRange &&
																																			dateRange.dateFrom
																																		}
																																		defaultDateTo={
																																			dateRange &&
																																			dateRange.dateTo
																																		}
																																		defaultDateRanges={
																																			options.dateRanges
																																		}
																																	>
																																		<DataContext.Provider
																																			value={
																																				dataContext
																																			}
																																		>
																																			<ApRedirectRoute
																																			>
																																				<Route>
																																					<React.Fragment
																																					>
																																						<HeaderWrapper
																																						// isLandingPage={
																																						// 	isLandingPage
																																						// }
																																						>
																																							<ApHeaderBar
																																								application={{
																																									color:
																																										'#7BD293',
																																									shortTitle:
																																										'SXR',
																																									title: (
																																										<HeaderTitle
																																										>
																																											{
																																												appName
																																											}{' '}
																																											|{' '}
																																											{
																																												<span
																																													style={{
																																														fontSize:
																																															'16px',
																																													}}
																																												>
																																													{
																																														companyName
																																													}
																																												</span>
																																											}{' '}
																																											{isDemoMode
																																												? ' (DEMO MODE)'
																																												: ''}
																																										</HeaderTitle>
																																									),
																																									link: analysisPageUrl,
																																								}}
																																								baseUrl={
																																									baseUrl
																																								}
																																								rightMenu={() => (
																																									<Route
																																										render={routeProps => (
																																											<Nav
																																												config={
																																													config
																																												}
																																												status={
																																													status
																																												}
																																												isPortco={
																																													false
																																												}
																																												permissions={
																																													permissions ||
																																													[]
																																												}
																																												onChangeContext={
																																													this
																																														.handleUpdateDataContext
																																												}
																																												{...routeProps}
																																											/>
																																										)}
																																									/>
																																								)}
																																							/>
																																						</HeaderWrapper>

																																						<Main
																																						// isLandingPage={
																																						// 	isLandingPage
																																						// }
																																						>
																																							{isTaxonomyApplying ? (
																																								<ApRedirectRoute
																																								>
																																									<Route
																																										exact
																																										path="/taxonomy"
																																										component={
																																											TaxonomyApplication
																																										}
																																									/>
																																									<Redirect to="/taxonomy" />
																																								</ApRedirectRoute>
																																							) : isUploadState ? (
																																								<ApRedirectRoute
																																								>
																																									{status.state ===
																																										UPLOAD_STATUS.CLASSIFIED &&
																																										!status.isUnpublishedDataExists &&
																																										permissions.includes(
																																											PERMISSIONS.SettingsWrite
																																										) && (
																																											<Route
																																												exact
																																												path="/settings"
																																												component={
																																													Settings
																																												}
																																											/>
																																										)}
																																									{status.state ===
																																										UPLOAD_STATUS.NO_DATA &&
																																										permissions.includes(
																																											PERMISSIONS.SettingsClassificationWrite
																																										) &&
																																										config &&
																																										config.isAlreadyExists && (
																																											<Route
																																												exact
																																												path="/settings/classification"
																																												component={
																																													Settings
																																												}
																																											/>
																																										)}
																																									{status.state ===
																																										UPLOAD_STATUS.CLASSIFIED &&
																																										!status.isUnpublishedDataExists &&
																																										permissions.includes(
																																											PERMISSIONS.SettingsWrite
																																										) && (
																																											<Redirect to="/settings" />
																																										)}
																																									{status.state ===
																																										UPLOAD_STATUS.NO_DATA &&
																																										permissions.includes(
																																											PERMISSIONS.SettingsClassificationWrite
																																										) && (
																																											<Redirect
																																												from="/settings"
																																												to="/settings/classification"
																																											/>
																																										)}
																																									{status.isUnpublishedDataExists &&
																																										permissions.includes(
																																											PERMISSIONS.UnpublishedDataRead
																																										) && (
																																											<Route
																																												path="/unpublished"
																																												component={
																																													UnpublishedData
																																												}
																																											/>
																																										)}
																																									{status.isUnpublishedDataExists &&
																																										permissions.includes(
																																											PERMISSIONS.UnpublishedDataRead
																																										) && (
																																											<Redirect to="/unpublished" />
																																										)}
																																									<Route
																																										path="/data"
																																										component={
																																											Data
																																										}
																																									/>
																																									{permissions.includes(
																																										PERMISSIONS.SettingsClassificationWrite
																																									) &&
																																										config &&
																																										!config.isAlreadyExists && (
																																											<Route
																																												exact
																																												path="/dpaas"
																																												render={() => (
																																													<ConfigureDpaas
																																														config={
																																															config
																																														}
																																														fetchClassification={
																																															fetchClassification
																																														}
																																													/>
																																												)}
																																											/>
																																										)}

																																									<Route
																																										path={[
																																											'/spend',
																																											'/pmi',
																																										]}
																																										render={() => (
																																											<NoData
																																												permissions={
																																													permissions
																																												}
																																												config={
																																													config
																																												}
																																											/>
																																										)}
																																									/>
																																									<Route
																																										path="/budget"
																																										component={
																																											BudgetTracker
																																										}
																																									/>
																																									{permissions.includes(
																																										PERMISSIONS.SpendControllerRead
																																									) && (
																																										<Route
																																											path="/spend-controller"
																																											component={
																																												SpendController
																																											}
																																										/>
																																									)}

																																									<Route
																																										path="/files/:fileName"
																																										component={
																																											Download
																																										}
																																									/>
																																									<Redirect
																																										to={
																																											isSendToData
																																												? '/data/append'
																																												: '/spend'
																																										}
																																									/>
																																								</ApRedirectRoute>
																																							) : (
																																								<ApRedirectRoute
																																								>
																																									<Route
																																										exact
																																										path="/"
																																									>
																																										<Redirect
																																											to={
																																												analysisPageUrl
																																											}
																																										/>
																																									</Route>
																																									{/* <Route
																																										path={
																																											landingPagePath
																																										}
																																										component={
																																											LandingPage
																																										}
																																									/> */}
																																									{!status.isUnpublishedDataExists && (
																																										<Redirect
																																											exact
																																											from="/unpublished"
																																											to="/spend/explore"
																																										/>
																																									)}

																																									<Route
																																										path="/files/:fileName"
																																										component={
																																											Download
																																										}
																																									/>
																																									{permissions.includes(
																																										PERMISSIONS.TaxonomyReleaseNotes
																																									) && (
																																										<Route
																																											path="/taxonomyviewer"
																																											render={() => (
																																												<TaxonomyViewer
																																													permissions={
																																														permissions
																																													}
																																												/>
																																											)}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.AnalysisRead
																																									) && (
																																										<Route
																																											path="/spend/explore"
																																											component={
																																												Explore
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.AnalysisRead
																																									) && (
																																										<Route
																																											path="/spend/pivot-table"
																																											component={
																																												PivotTable
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.CategoryRead
																																									) && (
																																										<Route
																																											path="/category-management"
																																											component={
																																												CategoryManagement
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.SpendControllerRead
																																									) && (
																																										<Route
																																											path="/spend-controller"
																																											component={
																																												SpendController
																																											}
																																										/>
																																									)}

																																									{permissions.includes(
																																										PERMISSIONS.AnalysisRead
																																									) && (
																																										<Route
																																											path="/benchmark/benchmarks"
																																											component={
																																												Benchmarks
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.AnalysisRead
																																									) && (
																																										<Route
																																											path="/market-analysis/suplier-diversity-database"
																																											component={
																																												SupplierDiversity
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.AnalysisRead
																																									) && (
																																										<Route
																																											path="/market-analysis/vendor-database"
																																											component={
																																												ApVendorLibrary
																																											}
																																										/>
																																									)}

																																									{permissions.includes(
																																										PERMISSIONS.UnpublishedDataRead
																																									) && (
																																										<Route
																																											path="/unpublished"
																																											component={
																																												UnpublishedData
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.AnalysisRead
																																									) && (
																																										<Route
																																											path="/spend"
																																											component={
																																												Investigate
																																											}
																																										/>
																																									)}

																																									<Route
																																										path="/data"
																																										component={
																																											Data
																																										}
																																									/>
																																									{permissions.includes(
																																										PERMISSIONS.SettingsWrite
																																									) && (
																																										<Route
																																											exact
																																											path="/settings/brand"
																																											component={
																																												Settings
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.AliasListRead
																																									) && (
																																										<Route
																																											exact
																																											path="/settings/alias-access"
																																											component={
																																												Settings
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.SettingsWrite
																																									) &&
																																										permissions.includes(
																																											PERMISSIONS.TransactionsWrite
																																										) && (
																																											<Route
																																												exact
																																												path="/settings/recycle"
																																												component={
																																													Settings
																																												}
																																											/>
																																										)}
																																									{permissions.includes(
																																										PERMISSIONS.SettingsWrite
																																									) &&
																																										permissions.includes(
																																											PERMISSIONS.TransactionsWrite
																																										) &&
																																										permissions.includes(
																																											PERMISSIONS.AccessControlRead
																																										) &&
																																										settings.isPortco && (
																																											<Route
																																												exact
																																												path="/settings/access-control"
																																												component={
																																													Settings
																																												}
																																											/>
																																										)}
																																									{permissions.includes(
																																										PERMISSIONS.SettingsWrite
																																									) && (
																																										<Route
																																											exact
																																											path="/settings/benchmarks"
																																											component={
																																												Settings
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.SettingsWrite
																																									) && (
																																										<Route
																																											exact
																																											path="/settings/bin"
																																											component={
																																												Settings
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.CCORead
																																									) && (
																																										<Route
																																											exact
																																											path="/settings/vendors"
																																											component={
																																												Settings
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.SettingsArchiveWrite
																																									) && (
																																										<Route
																																											path="/settings/archive"
																																											component={
																																												Settings
																																											}
																																										/>
																																									)}

																																									{permissions.includes(
																																										PERMISSIONS.SettingsClassificationWrite
																																									) && (
																																										<Route
																																											path="/settings/classification"
																																											component={
																																												Settings
																																											}
																																										/>
																																									)}

																																									{permissions.includes(
																																										PERMISSIONS.SettingsClassificationWrite
																																									) && (
																																										<Route
																																											path="/settings/company-metadata"
																																											component={
																																												Settings
																																											}
																																										/>
																																									)}

																																									{permissions.includes(
																																										PERMISSIONS.SettingsClassificationWrite
																																									) && (
																																										<Route
																																											path="/settings/spend-cube"
																																											component={
																																												Settings
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.SettingsWrite
																																									) &&
																																										!settings.isPortco && (
																																											<Route
																																												exact
																																												path="/settings/category-management"
																																												component={
																																													Settings
																																												}
																																											/>
																																										)}

																																									{permissions.includes(
																																										PERMISSIONS.SettingsClassificationWrite
																																									) &&
																																										permissions.includes(
																																											PERMISSIONS.AccessControlRead
																																										) &&
																																										settings.isPortco && (
																																											<Route
																																												path="/settings/access-control"
																																												component={
																																													Settings
																																												}
																																											/>
																																										)}

																																									{permissions.includes(
																																										PERMISSIONS.SettingsWrite
																																									) && (
																																										<Route
																																											exact
																																											path="/settings"
																																											render={routeProps => (
																																												<Settings
																																													{...routeProps}
																																													refetchSettings={() => {
																																														fetchSpendCube();
																																														fetchOptions();
																																													}}
																																												/>
																																											)}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.AnalysisRead
																																									) && (
																																										<Route
																																											path="/pmi"
																																											component={
																																												Pmi
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.BudgetRead
																																									) && (
																																										<Route
																																											path="/budget"
																																											component={
																																												BudgetTracker
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.InitiativesRead
																																									) && (
																																										<Route
																																											path="/initiatives/categories"
																																											component={
																																												Initiatives
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.InitiativesRead
																																									) && (
																																										<Route
																																											path="/initiatives/payment"
																																											component={
																																												Initiatives
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.InitiativesRead
																																									) && (
																																										<Redirect
																																											from="/initiatives"
																																											to="/initiatives/categories"
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.WavesRead
																																									) && (
																																										<Route
																																											path="/waves/allocate"
																																											component={
																																												InitiativesToWavesAllocation
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.WavesRead
																																									) && (
																																										<Route
																																											path="/waves"
																																											component={
																																												Waves
																																											}
																																										/>
																																									)}

																																									{!isTaxonomyApplyCompleted &&
																																									!isTaxonomyApplyNotStarted ? (
																																										<Route
																																											exact
																																											path="/taxonomy"
																																											component={
																																												TaxonomyApplication
																																											}
																																										/>
																																									) : (
																																										<Redirect
																																											from="/taxonomy"
																																											to={
																																												isTaxonomyApplyCompleted
																																													? '/spend'
																																													: '/taxonomymapper/direct'
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.TaxonomyApply
																																									) && (
																																										<Route
																																											path="/taxonomymapper/indirect"
																																											render={historyProps => (
																																												<IndirectTaxonomyMapper
																																													key="indirect"
																																													{...historyProps}
																																												/>
																																											)}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.TaxonomyApply
																																									) && (
																																										<Route
																																											path="/taxonomymapper/direct"
																																											render={historyProps => (
																																												<DirectTaxonomyMapper
																																													key="direct"
																																													{...historyProps}
																																												/>
																																											)}
																																										/>
																																									)}

																																									<Redirect
																																										from="/taxonomymapper"
																																										to="/taxonomymapper/direct"
																																									/>
																																									{permissions.includes(
																																										PERMISSIONS.TaxonomyApply
																																									) && (
																																										<Route
																																											path="/taxonomy-preview"
																																											component={
																																												TaxonomyPreview
																																											}
																																										/>
																																									)}
																																									{permissions.includes(
																																										PERMISSIONS.TaxonomyApply
																																									) && (
																																										<Route
																																											path="/taxonomy-preview-indirect"
																																											render={() => (
																																												<TaxonomyPreview
																																													apTaxonomy
																																												/>
																																											)}
																																										/>
																																									)}
																																									<Route
																																										path="/speed-to-market"
																																										component={
																																											SpeedToMarket
																																										}
																																									/>
																																									<Redirect
																																										to={
																																											analysisPageUrl
																																										}
																																									/>
																																								</ApRedirectRoute>
																																							)}
																																						</Main>
																																					</React.Fragment>
																																				</Route>
																																			</ApRedirectRoute>
																																		</DataContext.Provider>
																																	</QueryStringContextProvider>
																																);
																															}}
																														</SettingsContext.Consumer>
																													)}
																												</SpendCubeSettingsContext.Consumer>
																											</SpendCubeSettingsContextProvider>
																										)}
																									</SettingsOptionsContext.Consumer>
																								</SettingsOptionsContextProvider>
																							</SettingsContextProvider>
																						);
																					}}
																				</MetadataContext.Consumer>
																			</React.Fragment>
																		)}
																	</ClassificationContext.Consumer>
																)}
															</UploadStateContext.Consumer>
														)}
													</PermissionsContext.Consumer>
												</ApiDataContextProvider>
											</MetadataContextProvider>
										</UploadStateContextProvider>
									</UploadCategoryStateContextProvider>
								</UploadSkuStateContextProvider>
							</UploadVendorStateContextProvider>
						</ClassificationContextProvider>
					</PermissionsContextProvider>
				</Switch>
				<ApiErrorNotification />
			</React.Fragment>
		);
	}
}

const AppWithContext = props => (
	<ApAuthenticationContextConsumer>
		{({ user }) => <AppBase user={user} {...props} />}
	</ApAuthenticationContextConsumer>
);

export const App = withRouter(AppWithContext);
